import { isDesktop } from '@/consts/device';
import { Screen } from '@/types';
import { RightPanelTab } from '@/store/navigation/types';

import type { IAppStore } from '@/store';

export type PostGenerationDecision =
  | { kind: 'openLightbox'; fileId: string }
  | { kind: 'showToast' }
  | { kind: 'none' };

/**
 * Decides what to do after a generation completes:
 *  - `openLightbox`: the Generations panel is currently visible, so the
 *    Lightbox can open directly in result mode — no toast needed.
 *  - `showToast`: the panel is not visible, so we surface a notification.
 *  - `none`: the user is on the legacy loading screen; the auto-generation
 *    flow handles its own transition.
 *
 * Pure function — given the same screen/tab/fileId inputs, returns the same
 * decision. Caller is responsible for actually invoking the store actions.
 */
const decidePostGenerationUX = ({
  screen,
  rightPanelTab,
  fileId,
}: {
  screen: IAppStore['screen'];
  rightPanelTab: IAppStore['rightPanelTab'];
  fileId: string;
}): PostGenerationDecision => {
  // Auto-open lightbox in result mode when Generations panel is visible
  const canAutoOpenLightbox =
    screen === Screen.Files ||
    (isDesktop &&
      screen !== Screen.Loading &&
      rightPanelTab === RightPanelTab.Creations);

  if (canAutoOpenLightbox) {
    // Lightbox will open directly — no need for toast notification
    return { kind: 'openLightbox', fileId };
  }

  if (screen !== Screen.Loading) {
    // User is on a screen without the Generations panel — show toast
    return { kind: 'showToast' };
  }

  return { kind: 'none' };
};

export default decidePostGenerationUX;
